export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  articleCount: number;
  imageSeq: string;
}

export interface Article {
  id: string;
  title: string;
  slug: string;
  categoryId: string;
  categoryName: string;
  excerpt: string;
  content: ArticleSection[];
  author: string;
  date: string;
  readTime: string;
  views: number;
  tags: string[];
  imageSeq: string;
}

export interface ArticleSection {
  type: "paragraph" | "heading" | "list" | "image";
  content: string;
  items?: string[];
  alt?: string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
}

export const categories: Category[] = [
  {
    id: "cat-1",
    name: "注册指南",
    slug: "registration-guide",
    description: "详细的爱情岛网站注册教程，帮助新用户快速完成账号注册与实名认证，开启交友之旅。",
    icon: "ri-user-add-line",
    articleCount: 15,
    imageSeq: "category-registration-01",
  },
  {
    id: "cat-2",
    name: "功能教程",
    slug: "feature-tutorial",
    description: "全面解读爱情岛网站各项互动功能，包括匹配系统、私信聊天、礼物赠送与动态发布。",
    icon: "ri-guide-line",
    articleCount: 23,
    imageSeq: "category-feature-01",
  },
  {
    id: "cat-3",
    name: "安全指南",
    slug: "safety-guide",
    description: "保护您的账号安全与隐私信息，了解爱情岛网站的安全机制、防诈骗技巧与举报流程。",
    icon: "ri-shield-check-line",
    articleCount: 18,
    imageSeq: "category-safety-01",
  },
  {
    id: "cat-4",
    name: "交友技巧",
    slug: "dating-tips",
    description: "分享实用的在线交友经验与技巧，帮助您提升个人魅力，找到心仪的另一半。",
    icon: "ri-heart-line",
    articleCount: 31,
    imageSeq: "category-dating-01",
  },
  {
    id: "cat-5",
    name: "最新动态",
    slug: "latest-news",
    description: "爱情岛网站最新版本更新、活动公告与平台资讯，第一时间掌握官方动态。",
    icon: "ri-megaphone-line",
    articleCount: 12,
    imageSeq: "category-news-01",
  },
  {
    id: "cat-6",
    name: "常见问题",
    slug: "faq",
    description: "汇总用户在使用爱情岛网站过程中最常遇到的问题及解决方案，快速自助排查。",
    icon: "ri-question-answer-line",
    articleCount: 20,
    imageSeq: "category-faq-01",
  },
];

export const articles: Article[] = [
  {
    id: "article-1",
    title: "爱情岛网站注册全流程详解：三步完成账号创建",
    slug: "registration-complete-guide",
    categoryId: "cat-1",
    categoryName: "注册指南",
    excerpt: "本教程将手把手教您如何在爱情岛网站完成注册流程，从访问官网到填写个人信息，每一步都有详细说明，让您轻松开启交友之旅。",
    content: [
      { type: "heading", content: "第一步：访问爱情岛官方网站" },
      { type: "paragraph", content: "首先，您需要通过官方入口访问爱情岛网站。请务必使用最新官方地址，以确保访问安全。在浏览器地址栏输入官方网址后，您将看到爱情岛网站的首页。首页设计简洁大方，注册按钮位于页面右上角的显眼位置。建议使用Chrome、Edge或Safari等主流浏览器访问，以获得最佳的使用体验。" },
      { type: "heading", content: "第二步：填写注册信息" },
      { type: "paragraph", content: "点击「立即注册」按钮后，系统将引导您进入注册页面。您需要填写以下基本信息：手机号码、设置登录密码、输入验证码。爱情岛网站采用先进的加密技术保护您的个人信息，请放心填写。密码建议包含字母、数字和特殊字符的组合，长度不少于8位。" },
      { type: "heading", content: "第三步：完善个人资料" },
      { type: "paragraph", content: "注册成功后，系统会引导您完善个人资料。包括上传头像、填写昵称、选择性别、出生日期、所在城市等基本信息。完善度越高的资料，将获得更多的系统推荐机会。您还可以填写兴趣爱好、个性签名等扩展信息，让更多人了解真实的你。" },
      { type: "heading", content: "注册注意事项" },
      { type: "list", content: "注册过程中的重要提示：", items: ["请使用本人真实手机号注册，以便后续找回密码", "头像建议使用清晰的正面照片，提高交友成功率", "密码请妥善保管，不要泄露给他人", "完成实名认证可解锁更多平台功能", "注册即表示同意用户协议和隐私政策"] },
    ],
    author: "爱情岛小编",
    date: "2026-07-28",
    readTime: "8 分钟",
    views: 12580,
    tags: ["注册教程", "新手入门", "账号创建"],
    imageSeq: "article-registration-01",
  },
  {
    id: "article-2",
    title: "爱情岛网站智能匹配功能深度解析",
    slug: "smart-matching-feature",
    categoryId: "cat-2",
    categoryName: "功能教程",
    excerpt: "了解爱情岛网站的核心智能匹配算法，看看平台如何根据您的兴趣、性格和偏好，为您推荐最合适的朋友。",
    content: [
      { type: "heading", content: "智能匹配系统简介" },
      { type: "paragraph", content: "爱情岛网站的智能匹配系统是平台核心技术之一。该系统通过分析用户的个人资料、兴趣爱好、行为偏好等多维度数据，运用先进的算法模型，为用户推荐与其最匹配的其他用户。匹配准确率高达85%以上，受到了广大用户的一致好评。" },
      { type: "heading", content: "匹配算法的工作原理" },
      { type: "paragraph", content: "系统会综合考虑以下因素进行匹配计算：地理位置接近度（同城优先推荐）、年龄匹配度、兴趣爱好的重合程度、教育背景与职业领域、用户在平台上的活跃度与互动偏好。所有匹配计算均在后台自动完成，用户只需完善个人资料，系统便会持续优化推荐结果。" },
      { type: "heading", content: "如何提高匹配成功率" },
      { type: "list", content: "以下是提高匹配成功率的实用建议：", items: ["完善个人资料达到90%以上", "上传3-5张高质量生活照片", "填写详细的兴趣爱好标签", "保持每日活跃登录", "积极参与平台互动活动", "定期更新个人动态"] },
    ],
    author: "爱情岛小编",
    date: "2026-07-25",
    readTime: "10 分钟",
    views: 8930,
    tags: ["智能匹配", "功能教程", "交友推荐"],
    imageSeq: "article-matching-01",
  },
  {
    id: "article-3",
    title: "爱情岛网站安全防护机制全面解读",
    slug: "safety-protection-mechanism",
    categoryId: "cat-3",
    categoryName: "安全指南",
    excerpt: "深入了解爱情岛网站的多层安全防护体系，包括数据加密、实名认证、隐私保护和反诈骗机制。",
    content: [
      { type: "heading", content: "多层安全防护体系" },
      { type: "paragraph", content: "爱情岛网站始终将用户安全放在首位。平台构建了从数据加密、访问控制、行为监控到人工审核的四层安全防护体系，全面保障用户的信息安全和交友安全。所有用户数据传输均采用SSL/TLS加密协议，确保信息在传输过程中不被窃取或篡改。" },
      { type: "heading", content: "实名认证机制" },
      { type: "paragraph", content: "平台提供多种实名认证方式，包括手机号认证、身份证认证和人脸识别认证。完成实名认证的用户将获得认证标识，在搜索结果中更受信赖。所有认证信息均经过加密存储，且仅用于安全验证目的，不会被用于其他用途。" },
      { type: "heading", content: "防诈骗安全提示" },
      { type: "list", content: "请牢记以下安全交友原则：", items: ["切勿向陌生人透露银行卡号、密码等敏感信息", "警惕以投资、理财为名的诈骗行为", "首次见面建议选择公共场所", "遇到可疑行为请立即举报", "不要轻易点击陌生人发送的链接"] },
    ],
    author: "爱情岛小编",
    date: "2026-07-22",
    readTime: "7 分钟",
    views: 6720,
    tags: ["安全防护", "实名认证", "防诈骗"],
    imageSeq: "article-safety-01",
  },
  {
    id: "article-4",
    title: "在线交友第一印象打造指南",
    slug: "first-impression-guide",
    categoryId: "cat-4",
    categoryName: "交友技巧",
    excerpt: "在爱情岛网站打造出色的个人形象是成功交友的第一步。本文为您分享如何优化个人资料、选择头像和撰写签名。",
    content: [
      { type: "heading", content: "为什么第一印象如此重要" },
      { type: "paragraph", content: "在在线交友平台上，第一印象几乎决定了对方是否愿意与你进一步交流。研究表明，用户在浏览交友资料时，平均只花费3-5秒来决定是否点击查看详情。因此，一个精心打造的个人资料至关重要。" },
      { type: "heading", content: "头像选择技巧" },
      { type: "paragraph", content: "头像是个人资料中最重要的元素。建议选择清晰的正面照片，背景简洁，光线充足。最好使用近半年内的生活照，展现真实的自己。避免使用过度美颜或滤镜的照片，真实自然才是最好的。" },
      { type: "heading", content: "个性签名撰写建议" },
      { type: "list", content: "优秀的个性签名应该：", items: ["简短有力，控制在50字以内", "展现真实的兴趣爱好和性格特点", "适当加入幽默元素，让人印象深刻", "表达你对理想关系的期待", "避免负面或消极的表达"] },
    ],
    author: "爱情岛小编",
    date: "2026-07-20",
    readTime: "6 分钟",
    views: 10450,
    tags: ["交友技巧", "个人形象", "头像选择"],
    imageSeq: "article-dating-01",
  },
  {
    id: "article-5",
    title: "2026年爱情岛网站夏季版本更新公告",
    slug: "2026-summer-update",
    categoryId: "cat-5",
    categoryName: "最新动态",
    excerpt: "爱情岛网站2026年夏季大版本更新正式上线！全新UI界面、增强的匹配算法、视频聊天功能等多项重磅升级。",
    content: [
      { type: "heading", content: "全新UI界面升级" },
      { type: "paragraph", content: "本次更新对平台整体界面进行了全面优化，采用了更加简洁现代的设计语言。新的配色方案更加柔和舒适，图标和按钮尺寸经过精心调整，操作更加便捷。暗黑模式也首次加入，保护用户在夜间使用时的视力健康。" },
      { type: "heading", content: "匹配算法2.0" },
      { type: "paragraph", content: "升级后的匹配算法引入了深度学习模型，匹配精度提升约30%。新算法能够更准确地理解用户的行为偏好，并据此进行个性化推荐。同时，新增的「缘分值」功能，让用户可以直观地看到与推荐对象的匹配程度。" },
      { type: "heading", content: "视频聊天功能上线" },
      { type: "paragraph", content: "备受期待的视频聊天功能正式上线。用户可以在双方同意的情况下，发起一对一视频通话。视频聊天采用端到端加密技术，确保通话内容安全私密。该功能目前支持高清画质，并可根据网络状况自动调节清晰度。" },
    ],
    author: "爱情岛小编",
    date: "2026-07-15",
    readTime: "5 分钟",
    views: 15200,
    tags: ["版本更新", "新功能", "视频聊天"],
    imageSeq: "article-update-01",
  },
  {
    id: "article-6",
    title: "如何快速找到爱情岛网站最新官方入口",
    slug: "find-official-access",
    categoryId: "cat-6",
    categoryName: "常见问题",
    excerpt: "解答用户关于爱情岛网站访问入口的常见疑问，帮助您快速、安全地找到最新官方访问地址。",
    content: [
      { type: "heading", content: "官方入口获取方式" },
      { type: "paragraph", content: "为确保您始终能够安全访问爱情岛网站，我们提供多种官方入口获取途径。最推荐的方式是收藏官方域名，或关注我们的官方社交媒体账号获取最新动态。平台也提供官方App下载，通过应用商店搜索即可找到。" },
      { type: "heading", content: "访问异常处理" },
      { type: "paragraph", content: "如果您发现无法正常访问爱情岛网站，请先检查网络连接是否正常。如果网络正常但仍无法访问，建议清除浏览器缓存后重试，或尝试使用其他浏览器访问。若问题持续存在，可联系客服获取帮助。" },
      { type: "heading", content: "常见问题解答" },
      { type: "list", content: "用户经常咨询的问题：", items: ["推荐使用Chrome浏览器访问", "建议收藏官方网址以便快速访问", "关注官方公众号获取最新入口通知", "如遇访问异常请及时联系在线客服", "请勿使用非官方渠道获取的入口地址"] },
    ],
    author: "爱情岛小编",
    date: "2026-07-10",
    readTime: "4 分钟",
    views: 21300,
    tags: ["访问入口", "常见问题", "官方地址"],
    imageSeq: "article-access-01",
  },
];

export const faqs: FAQ[] = [
  {
    id: "faq-1",
    question: "爱情岛网站是免费的吗？",
    answer: "爱情岛网站提供免费注册和基础功能使用。您可以通过免费注册创建账号、浏览用户资料、使用基础匹配功能。平台也提供VIP会员服务，VIP会员可享受无限私信、高级搜索筛选、优先推荐等增值功能。新用户注册即享3天VIP体验特权。",
  },
  {
    id: "faq-2",
    question: "如何保证账号安全？",
    answer: "爱情岛网站采用多重安全保护措施：所有数据采用SSL加密传输，用户密码经过加密哈希存储，支持手机验证码登录和指纹/面部识别登录（App端）。建议您开启双重验证功能，定期修改密码，并不要与他人共享账号。",
  },
  {
    id: "faq-3",
    question: "注册需要提供哪些信息？",
    answer: "基础注册只需提供手机号码和设置密码即可完成。为了获得更好的交友体验，建议您在注册后完善个人资料，包括昵称、头像、性别、年龄、所在城市、兴趣爱好等。所有信息均可随时修改，平台严格保护您的隐私。",
  },
  {
    id: "faq-4",
    question: "如何举报不良用户？",
    answer: "如果您在使用过程中遇到骚扰、诈骗或其他不良行为，可以在该用户的主页点击「举报」按钮，选择举报原因并提交。我们的审核团队会在24小时内处理您的举报。您也可以将不良用户加入黑名单，对方将无法再与您联系。",
  },
  {
    id: "faq-5",
    question: "可以注销账号吗？",
    answer: "可以的。在「设置」-「账号管理」中选择「注销账号」即可。注销后，您的所有个人资料、聊天记录将被永久删除，且无法恢复。如果您只是暂时不想使用，也可以选择「冻结账号」功能，冻结期间其他用户无法搜索到您。",
  },
  {
    id: "faq-6",
    question: "爱情岛网站支持哪些设备访问？",
    answer: "爱情岛网站支持全平台访问，包括PC网页版（Windows/Mac）、移动网页版（H5）、iOS App和Android App。所有平台账号互通，数据实时同步，您可以在任意设备上随时登录使用。建议从官方应用商店下载App以获得最佳体验。",
  },
  {
    id: "faq-7",
    question: "如何联系客服？",
    answer: "您可以通过以下方式联系我们的客服团队：在线客服（平台内右下角客服图标）、官方邮箱（support@aiqingdao.com）、官方微信公众号（搜索「爱情岛」）。客服工作时间：周一至周日 9:00-22:00，节假日正常服务。",
  },
  {
    id: "faq-8",
    question: "平台上的用户是真实的吗？",
    answer: "爱情岛网站严格推行实名认证机制，所有注册用户均需通过手机号验证。我们采用AI智能审核与人工审核相结合的方式，持续清理虚假账号和违规内容，确保平台交友环境的真实性和安全性。目前平台真实用户率超过98%。",
  },
];

export const features = [
  {
    id: "feat-1",
    title: "智能匹配",
    description: "基于多维度的智能算法，为您精准推荐志趣相投的朋友，让相遇更加高效。",
    icon: "ri-heart-2-line",
  },
  {
    id: "feat-2",
    title: "安全可靠",
    description: "多层安全防护体系，实名认证机制，全方位保护您的个人信息和交友安全。",
    icon: "ri-shield-star-line",
  },
  {
    id: "feat-3",
    title: "即时通讯",
    description: "支持文字、语音、图片和视频聊天，让沟通更加生动，距离不再成为障碍。",
    icon: "ri-message-2-line",
  },
  {
    id: "feat-4",
    title: "动态社区",
    description: "分享生活点滴，发现有趣灵魂。在动态广场展示真实的自己，邂逅美好缘分。",
    icon: "ri-compass-3-line",
  },
  {
    id: "feat-5",
    title: "活动派对",
    description: "线上线下丰富的主题活动，让交友不再局限于屏幕，创造更多相遇的机会。",
    icon: "ri-calendar-event-line",
  },
  {
    id: "feat-6",
    title: "隐私保护",
    description: "灵活的隐私设置选项，您可以自主控制个人信息的可见范围，安心交友。",
    icon: "ri-lock-line",
  },
];

export const stats = [
  { value: "500万+", label: "注册用户" },
  { value: "98%", label: "真实用户率" },
  { value: "30万+", label: "日均活跃" },
  { value: "85%", label: "匹配成功率" },
];

export const testimonials = [
  {
    id: "test-1",
    name: "小雨",
    age: 26,
    city: "北京",
    content: "在爱情岛遇到了现在的男朋友，已经在一起半年了！平台的匹配功能真的超级准，推荐的几个男生都很符合我的预期。感谢爱情岛让我找到真爱！",
    avatar: "https://readdy.ai/api/search-image?query=Young%20Chinese%20woman%20portrait%20with%20warm%20smile%20soft%20natural%20lighting%20clean%20background%20simple%20composition&width=120&height=120&seq=testimonial-avatar-01&orientation=squarish",
  },
  {
    id: "test-2",
    name: "浩然",
    age: 29,
    city: "上海",
    content: "之前用过好几个交友软件都不太靠谱，直到朋友推荐了爱情岛。这里的用户质量明显更高，大家都比较真诚，客服响应也很快。现在已经成功脱单啦！",
    avatar: "https://readdy.ai/api/search-image?query=Young%20Chinese%20man%20portrait%20with%20friendly%20expression%20natural%20light%20simple%20background%20clean%20composition&width=120&height=120&seq=testimonial-avatar-02&orientation=squarish",
  },
  {
    id: "test-3",
    name: "思涵",
    age: 24,
    city: "广州",
    content: "作为一个社恐人士，在线交友对我来说是最舒服的方式。爱情岛的界面设计很友好，功能也不会太复杂。注册简单，体验流畅，推荐给所有想要认真交友的朋友。",
    avatar: "https://readdy.ai/api/search-image?query=Young%20Chinese%20woman%20with%20gentle%20smile%20portrait%20soft%20studio%20lighting%20minimal%20background%20professional%20headshot&width=120&height=120&seq=testimonial-avatar-03&orientation=squarish",
  },
];